import React, { type FunctionComponent, useState, useRef } from 'react'
import axios from 'axios'
import { HelpIcon } from '@instacart/ids-core'
import {
  Alert,
  Button,
  colors,
  FileUploader,
  Modal,
  ModalBody,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  NotificationLevel,
  notify,
  spacing,
  Text,
  Tooltip,
} from '@retailer-platform/shared-components'
import styled from '@emotion/styled'
import {
  type FileUploaderDropAreaComponentProps,
  type FileUploaderDroppedFileComponentProps,
  type FileUploaderErrorComponentProps,
} from '@retailer-platform/shared-components/src/common/file-uploader/utils/fileUploader.types'
import { parse, type ParseError } from 'papaparse'
import { Divider } from '@retailer-platform/shared-components/src/tds'
import faker from 'faker'
import { LoadingSpinner, SwitchBase } from '@instacart/ids-customers'
import { useDomainMessages } from '../../../utils/domain/intl'
import { useGetPresignedUploadUrlQuery, useRunManualStepTestMutation } from '../../../api'
import { TooltipContainerNotFullWidth } from '../../shared-styled-components/SharedStyledComponents'

export const SwitchRow = styled.div({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: `${spacing.X12}`,
})

interface Props {
  onClose: () => void
  stepVersionId: string
  expectedFields: string[]
}

const Row = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
})

const StyledButton = styled(Button)({
  margin: `${spacing.X0}`,
})
const StyledModalBody = styled(ModalBody)({
  gap: `${spacing.X8}`,
})

const RegexRunManualTestModal: FunctionComponent<React.PropsWithChildren<Props>> = ({
  onClose,
  stepVersionId,
  expectedFields,
}) => {
  const i18n = useDomainMessages({
    title: 'catalogAdminDomain.attributeManagement.runTests.modal.title',
    submit: 'catalogAdminDomain.attributeManagement.runTests.modal.button.submit',
    cancel: 'catalogAdminDomain.attributeManagement.runTests.modal.button.cancel',
    upload: 'catalogAdminDomain.attributeManagement.runTests.modal.upload.button',
    download: 'catalogAdminDomain.attributeManagement.runTests.modal.body.button.download',
    fileDescription: 'catalogAdminDomain.attributeManagement.runTests.modal.body.description',
    templateDescription:
      'catalogAdminDomain.attributeManagement.runTests.modal.body.template.description',
    noFileSelected: 'catalogAdminDomain.attributeManagement.runTests.modal.upload.noFileSelected',
    update: 'catalogAdminDomain.attributeManagement.runTests.modal.body.button.update',
    uploadingFile: 'catalogAdminDomain.attributeManagement.runTests.modal.uploadingFile',
    batchSizeTooltip:
      'catalogAdminDomain.attributeManagement.runTests.modal.body.tooltip.batchSize',
    runType: 'catalogAdminDomain.attributeManagement.runResults.modal.subheader.runType',
    limitedBatchTest:
      'catalogAdminDomain.attributeManagement.runResults.modal.body.limitedBatchTest',
    largeBatchTest: 'catalogAdminDomain.attributeManagement.runResults.modal.body.largeBatchTest',
  })
  const [file, setFile] = useState<Record<any, any>[] | null>(null)
  const [rawFile, setRawFile] = useState<File | null>(null)
  const [validSmallFile, setValidSmallFile] = useState<boolean>(true)
  const [isLargeBatch, setIsLargeBatch] = useState(false)
  const [fileProcessing, setFileProcessing] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const idempotencyKey = useRef(faker.random.uuid())

  const { data } = useGetPresignedUploadUrlQuery({
    variables: {
      clientId: 'catalog_camp_ipp',
      idempotencyKey: idempotencyKey.current,
    },
    onError: e => {
      notify({
        level: NotificationLevel.Error,
        contents: `Error getting presigned url: ${e.message}`,
      })
    },
  })
  const presignedUrl =
    data?.instacart_catalog_catflow_v1_CatflowAPI_GetPresignedUploadUrl?.url || ''
  const inputS3File = data?.instacart_catalog_catflow_v1_CatflowAPI_GetPresignedUploadUrl?.key || ''
  const uploadFile = async () => {
    setIsUploading(true)
    try {
      const response = await axios.put(presignedUrl, rawFile, {
        headers: {
          'Content-Type': 'text/csv',
          'Access-Control-Allow-Origin': '*',
          'Access-Control-Allow-Methods': 'PUT, OPTIONS',
          'Access-Control-Allow-Headers': 'Content-Type',
        },
      })
      if (response.status !== 200) {
        throw new Error(`Error uploading file to S3: Status ${response.status}`)
      }
    } finally {
      setIsUploading(false)
    }
  }

  const handleSubmit = async () => {
    if (isLargeBatch) {
      try {
        await uploadFile()
        triggerManualTest()
      } catch (error: unknown) {
        notify({
          level: NotificationLevel.Error,
          contents: `Error processing request: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`,
        })
        setIsUploading(false)
        onClose()
        return
      }
    } else {
      triggerManualTest()
    }
  }

  const [triggerManualTest, { loading }] = useRunManualStepTestMutation({
    variables: {
      clientId: 'catalog_camp_ipp',
      idempotencyKey: faker.random.uuid(),
      stepVersionId: stepVersionId,
      testInputs: isLargeBatch ? [] : file,
      inputS3File: isLargeBatch ? inputS3File : '',
      isTest: true,
    },
    onError: e => {
      setIsUploading(false)
      notify({
        level: NotificationLevel.Error,
        contents: `Error running manual test: ${e.message}`,
      })
    },
    onCompleted: () => {
      setIsUploading(false)
      onClose()
    },
  })
  const DropAreaComponent = ({ messages }: FileUploaderDropAreaComponentProps) => (
    <Row>
      <Text> {i18n.noFileSelected}</Text>
    </Row>
  )
  const DroppedFileComponent = ({ fileName }: FileUploaderDroppedFileComponentProps) => (
    <Row>
      <Text>{fileName}</Text>
      <Button variant="tertiary">{i18n.update}</Button>
    </Row>
  )
  const ErrorComponent = (props: FileUploaderErrorComponentProps) => (
    <Row>
      <Text>{props.fileName}</Text>
      <Text css={{ color: colors.DETRIMENTAL.REGULAR }}>{props.messages.error}</Text>
      <Button>{props.messages.retry}</Button>
    </Row>
  )

  const droppedAreaComponentMessages = useDomainMessages({
    uploadButton: 'catalogAdminDomain.attributeManagement.runTests.modal.upload.button',
    uploadText: 'catalogAdminDomain.attributeManagement.runTests.modal.upload.dragDrop',
    or: 'catalogAdminDomain.attributeManagement.runTests.modal.upload.or',
  })

  const errorComponentMessages = useDomainMessages({
    fileTypeError:
      'catalogAdminDomain.attributeManagement.runTests.modal.upload.error.fileTypeError',
    fileContentError:
      'catalogAdminDomain.attributeManagement.runTests.modal.upload.error.fileContentError',
    uploadError: 'catalogAdminDomain.attributeManagement.runTests.modal.upload.error.uploadError',
    error: 'catalogAdminDomain.attributeManagement.runTests.modal.upload.error.error',
    retry: 'catalogAdminDomain.attributeManagement.runTests.modal.upload.error.retry',
    rowError: 'catalogAdminDomain.attributeManagement.runTests.modal.upload.error.rowError',
  })

  const isUploadButtonDisabled =
    fileProcessing ||
    errorMessage !== '' ||
    (isLargeBatch && rawFile == null) ||
    (!isLargeBatch && validSmallFile == null)

  const onDownloadTemplate = () => {
    const headers = ['entity_id', 'entity_type', ...expectedFields]
    const csvContent = headers.join(',') + '\n'
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'template.csv')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const parseFile = (file: File) => {
    setFileProcessing(true)
    setRawFile(file)
    parse(file, {
      worker: true,
      header: true,
      skipEmptyLines: true,
      complete: result => {
        setFileProcessing(false)
        const valid = result.data.length > 0 && result.data.length <= 100
        setValidSmallFile(valid)
        if (valid) {
          const formattedData = result.data.map(row => {
            const formattedRow = Object.fromEntries(
              Object.entries(row).map(([key, value]) => [key, value])
            )
            return formattedRow
          })
          setFile(formattedData)
          setErrorMessage('')
        }
      },
      error: (error: ParseError) => {
        setFileProcessing(false)
        setErrorMessage(error.message)
      },
    })
  }

  return (
    <>
      <Modal isOpen={true} hideCloseButton={true} onRequestClose={onClose}>
        <ModalContainer>
          <ModalHeader>{i18n.title}</ModalHeader>
          <StyledModalBody>
            <SwitchRow>
              <Text css={{ fontWeight: 'bold' }}>{i18n.runType} </Text>
              <TooltipContainerNotFullWidth>
                <Tooltip placement="bottom" target={<HelpIcon />}>
                  {i18n.batchSizeTooltip}
                </Tooltip>
              </TooltipContainerNotFullWidth>
            </SwitchRow>
            <SwitchRow>
              <Text css={{ color: isLargeBatch ? colors.GRAYSCALE.X30 : colors.SECONDARY.DARK }}>
                {' '}
                {i18n.limitedBatchTest}
              </Text>
              <SwitchBase
                checked={isLargeBatch}
                onChange={() => {
                  setIsLargeBatch(!isLargeBatch)
                }}
                description="Run a large batch"
              />
              <Text css={{ color: isLargeBatch ? colors.SECONDARY.DARK : colors.GRAYSCALE.X30 }}>
                {' '}
                {i18n.largeBatchTest}
              </Text>
            </SwitchRow>
            <Divider></Divider>
            <Text size="medium">{i18n.templateDescription} </Text>

            <StyledButton variant="secondary" compact={true} onClick={onDownloadTemplate}>
              {i18n.download}
            </StyledButton>
            <Divider></Divider>
            {isUploading ? <Text>{i18n.uploadingFile} </Text> : <Text>{i18n.upload} </Text>}
            {isUploading && <LoadingSpinner />}

            {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
            {!isLargeBatch && !validSmallFile && (
              <Alert variant="danger">{errorComponentMessages.rowError}</Alert>
            )}

            {!isUploading && (
              <FileUploader
                accept=".csv"
                DropAreaComponent={DropAreaComponent}
                uploadButtonProps={{
                  dropAreaButton: {
                    variant: 'secondary',
                    uploadButton: i18n.upload,
                  },
                  droppedFileButton: {
                    variant: 'secondary',
                    uploadButton: i18n.update,
                  },
                  errorButton: {
                    variant: 'secondary',
                    uploadButton: errorComponentMessages.retry,
                  },
                }}
                onUpload={parseFile}
                DroppedFileComponent={DroppedFileComponent}
                ErrorComponent={ErrorComponent}
                droppedAreaComponentMessages={droppedAreaComponentMessages}
                errorComponentMessages={errorComponentMessages}
              />
            )}
          </StyledModalBody>

          <ModalFooter>
            <Button
              variant={'primary'}
              disabled={isUploadButtonDisabled || loading}
              onClick={() => handleSubmit()}
            >
              {i18n.submit}
            </Button>
            <Button variant={'secondary'} onClick={() => onClose()}>
              {i18n.cancel}
            </Button>
          </ModalFooter>
        </ModalContainer>
      </Modal>
      {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
    </>
  )
}

export default RegexRunManualTestModal
